import type { BrowseState, BrowseStateConfig, FilterDefinition } from '@/browse/browse_state'
import { DEFAULT_ALT_SKILL_LEVEL } from '@/presentlevels/api_v1.js'
import type { DefaultStatement, Skill } from '@/presentlevels/no-ai/skill_statement_types'

const ACADEMIC_SUBJECTS = ['Reading', 'Writing', 'Math']
const MASTERY_LEVEL = 'Mastery'

export type SubjectType = 'Academic' | 'Alt' | 'Other'

export type LevelLayout = {
  columns: number
  labelPosition: 'top' | 'right'
}

export interface PresentLevelsBrowseContext {
  grades: () => string[]
  subjects: () => string[]
  // Undefined while a subject's categories or a category's statements are still loading.
  categoryUuidsFor: (subject: string) => string[] | undefined
  levelLabelsFor: (categoryUuid: string) => string[] | undefined
}

export function selectedGradeOf({ refinements }: BrowseState): string {
  return refinements.grade[0] ?? ''
}

export function selectedSubjectOf({ refinements }: BrowseState): string {
  return refinements.subject[0] ?? ''
}

export function selectedCategoryOf({ refinements }: BrowseState): string {
  return refinements.category[0] ?? ''
}

export function selectedLevelOf({ refinements }: BrowseState): string {
  return refinements.level[0] ?? ''
}

// No subject has no categories, and no category has no levels.
function categoryUuidsForSelectedSubject(context: PresentLevelsBrowseContext, state: BrowseState): string[] | undefined {
  const subject = selectedSubjectOf(state)
  if (!subject) {
    return []
  }
  return context.categoryUuidsFor(subject)
}

function levelLabelsForSelectedCategory(context: PresentLevelsBrowseContext, state: BrowseState): string[] | undefined {
  const categoryUuid = selectedCategoryOf(state)
  if (!categoryUuid) {
    return []
  }
  return context.levelLabelsFor(categoryUuid)
}

// Each step of the drill-down depends on the one before it: a new grade changes the default
// level, a new subject has its own categories, and a new category its own levels.
function presentLevelsFacets(context: PresentLevelsBrowseContext): FilterDefinition[] {
  return [
    { key: 'grade', clearsOnChange: ['level'], validValues: () => context.grades() },
    { key: 'subject', clearsOnChange: ['category'], validValues: () => context.subjects() },
    { key: 'category', clearsOnChange: ['level'], validValues: (state) => categoryUuidsForSelectedSubject(context, state) },
    { key: 'level', validValues: (state) => levelLabelsForSelectedCategory(context, state) }
  ]
}

export function presentLevelsBrowseConfig(context: PresentLevelsBrowseContext): BrowseStateConfig {
  return { facets: presentLevelsFacets(context) }
}

export function withUnlockedCategoriesFirst(categories: Skill[]): Skill[] {
  return [...categories].sort((a, b) => Number(Boolean(a.locked)) - Number(Boolean(b.locked)))
}

export function firstUnlockedCategory(categories: Skill[]): Skill | undefined {
  return categories.find((category) => !category.locked)
}

export function firstUnlockedStatement(statements: DefaultStatement[]): DefaultStatement | undefined {
  return statements.find((statement) => !statement.locked)
}

function hasAltLevels(statements: DefaultStatement[]): boolean {
  return statements.some((statement) => statement.level_label === DEFAULT_ALT_SKILL_LEVEL)
}

export function subjectTypeFor(subject: string, statements: DefaultStatement[]): SubjectType | undefined {
  if (!subject) {
    return undefined
  }
  if (ACADEMIC_SUBJECTS.includes(subject)) {
    return 'Academic'
  }
  if (hasAltLevels(statements)) {
    return 'Alt'
  }
  return 'Other'
}

export function levelLayoutFor(subjectType: SubjectType | undefined): LevelLayout {
  switch (subjectType) {
    case 'Academic':
      return { columns: 14, labelPosition: 'top' }
    case 'Alt':
      return { columns: 6, labelPosition: 'top' }
    default:
      return { columns: 2, labelPosition: 'right' }
  }
}

const SCALAR_BY_NAMED_LEVEL: Record<string, number> = { 'Pre-K': -1, K: 0, [MASTERY_LEVEL]: 13 }
const ORDINAL_SUFFIX = /st|nd|rd|th/gi

// Puts grade levels on one number line ('Pre-K' -1, 'K' 0, '3rd' 3, 'Mastery' 13) so a
// student's grade can be compared with a category's levels.
export function scalarLevel(levelLabel: string): number {
  if (levelLabel in SCALAR_BY_NAMED_LEVEL) {
    return SCALAR_BY_NAMED_LEVEL[levelLabel]
  }
  return parseInt(levelLabel.replace(ORDINAL_SUFFIX, ''))
}

function lowestLevel(statements: DefaultStatement[]): string {
  const [lowest] = [...statements].sort((a, b) => Number(a.level_order) - Number(b.level_order))
  return lowest.level_label
}

// The highest level below Mastery, which is always the top level.
function highestGradeLevel(statements: DefaultStatement[]): number {
  const scalarLevels = statements.map((statement) => scalarLevel(statement.level_label)).sort((a, b) => a - b)
  return scalarLevels[scalarLevels.length - 2]
}

// An academic statement is written for a grade, so the student's own grade is the default, Mastery
// when the grade is above every level, and the lowest level when it is below them.
function academicDefaultLevel(statements: DefaultStatement[], grade: string): string | undefined {
  const studentLevel = scalarLevel(grade)
  const levelAtStudentGrade = statements.find((statement) => scalarLevel(statement.level_label) === studentLevel)
  if (levelAtStudentGrade) {
    return levelAtStudentGrade.level_label
  }
  if (studentLevel > highestGradeLevel(statements)) {
    return MASTERY_LEVEL
  }
  return lowestLevel(statements)
}

export interface DefaultLevelInput {
  subjectType: SubjectType | undefined
  statements: DefaultStatement[]
  grade: string
}

// Undefined leaves the level unchosen. An academic subject has none until a grade is chosen,
// so the page keeps asking for the student's grade first.
export function defaultLevelFor({ subjectType, statements, grade }: DefaultLevelInput): string | undefined {
  if (!statements.length) {
    return undefined
  }
  if (subjectType === 'Alt') {
    return DEFAULT_ALT_SKILL_LEVEL
  }
  if (subjectType !== 'Academic') {
    return lowestLevel(statements)
  }
  if (!grade) {
    return undefined
  }
  return academicDefaultLevel(statements, grade)
}
